
// ABOUTME: Custom hook for fetching and managing job roles data
// ABOUTME: Provides CRUD operations for job roles including create, update, and delete functionality

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Database } from '@/integrations/supabase/types';

type JobRole = Database['public']['Tables']['job_roles']['Row'];
type JobRoleInsert = Database['public']['Tables']['job_roles']['Insert'];
type JobRoleUpdate = Database['public']['Tables']['job_roles']['Update'];

// Enhanced type to include referral statistics
export type JobRoleWithStats = JobRole & {
  total_referrals: number;
  active_referrals: number;
  hired_count: number;
  required_tools: string[] | null;
  essential_skills: string | null;
  Company?: string | null;
  Essential_Skills?: string | null;
  Key_deliverables?: string | null;
  Required_tools?: string | null;
};

export function useJobRoles() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: jobRoles, isLoading } = useQuery({
    queryKey: ['job-roles-with-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_roles')
        .select(`
          *,
          referrals:referrals(
            id,
            status
          )
        `)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include calculated statistics
      const jobRolesWithStats: JobRoleWithStats[] = (data || []).map(role => {
        const referrals = role.referrals || [];
        const total_referrals = referrals.length;
        const active_referrals = referrals.filter(ref => 
          ['submitted', 'under_review', 'interview_scheduled', 'interviewed'].includes(ref.status || '')
        ).length;
        const hired_count = referrals.filter(ref => ref.status === 'hired').length;

        return {
          ...role,
          total_referrals,
          active_referrals,
          hired_count,
          required_tools: (role as any).Required_tools || (role as any).required_tools || null,
          essential_skills: (role as any).Essential_Skills || null,
          Company: (role as any).Company || null,
          Essential_Skills: (role as any).Essential_Skills || null,
          Key_deliverables: (role as any).Key_deliverables || null,
          Required_tools: (role as any).Required_tools || null
        };
      });

      return jobRolesWithStats;
    },
  });

  const createJobRole = useMutation({
    mutationFn: async (newRole: JobRoleInsert) => {
      const roleWithCreator = {
        ...newRole,
        created_by: user?.id
      };
      
      const { data, error } = await supabase
        .from('job_roles')
        .insert(roleWithCreator)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-roles-with-stats'] });
    },
  });

  const updateJobRole = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & JobRoleUpdate) => {
      const { data, error } = await supabase
        .from('job_roles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-roles-with-stats'] });
    },
  });

  return {
    jobRoles,
    isLoading,
    createJobRole,
    updateJobRole,
  };
}
